import { useEffect, useState, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { useStoreInfo } from '../../store/useStoreInfo';
import * as S from './styles';

type DayPeriod = 'am' | 'pm';

/** يضيف مسافة بعد HH:MM إذا كانت ملتصقة بالنص (مثل 1:00بعد → 1:00 بعد). */
const formatWorkingHourDisplay = (timeStr: string): string =>
  timeStr.replace(/^(\d{1,2}:\d{2})(?=\S)/, '$1 ');

const parseDayPeriod = (period: string): DayPeriod | null => {
  const normalized = period.trim().toLowerCase();
  if (
    normalized === 'ص' ||
    normalized.startsWith('صباح') ||
    normalized === 'am' ||
    normalized === 'a.m.' ||
    normalized.includes('بعد منتصف') ||
    normalized.includes('after midnight')
  ) {
    return 'am';
  }
  if (
    normalized === 'م' ||
    normalized.startsWith('مساء') ||
    normalized === 'pm' ||
    normalized === 'p.m.' ||
    normalized.includes('قبل منتصف') ||
    normalized.includes('before midnight')
  ) {
    return 'pm';
  }
  return null;
};

const getRiyadhNow = (): Date => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Riyadh',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date());

  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '0';
  return new Date(
    parseInt(get('year'), 10),
    parseInt(get('month'), 10) - 1,
    parseInt(get('day'), 10),
    parseInt(get('hour'), 10),
    parseInt(get('minute'), 10),
    parseInt(get('second'), 10),
  );
};

const parseTime = (timeStr: string, now: Date): Date | null => {
  try {
    const trimmed = timeStr.trim();
    const timeMatch = trimmed.match(/^(\d{1,2}:\d{2})/);
    if (!timeMatch) return null;

    const time = timeMatch[1];
    const period = parseDayPeriod(trimmed.slice(timeMatch[0].length));
    if (!period) return null;

    const [hoursStr, minutesStr] = time.split(':');
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (period === 'pm' && hours !== 12) {
      hours += 12;
    } else if (period === 'am' && hours === 12) {
      hours = 0;
    }

    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0, 0);
  } catch (e) {
    // console.error('Error parsing time:', e);
    return null;
  }
};

const checkIsOpen = (openStr: string, closeStr: string, manualStatus?: string): boolean => {
  if (manualStatus?.trim() === 'مغلق') return false;
  if (!openStr || !closeStr) return false;

  const now = getRiyadhNow();
  const openTime = parseTime(openStr, now);
  const closeTime = parseTime(closeStr, now);

  if (!openTime || !closeTime) return false;

  // Handle case where closing time is after midnight (e.g. 10 AM to 2 AM)
  if (closeTime < openTime) {
    // If current time is after open time (e.g. 11 PM), it's open
    if (now >= openTime) {
        // closeTime needs to be next day for comparison logic if we wanted strictly range check, 
        // but simple logic: it's open if >= open OR <= close (on next day)
        // Actually, if close < open, the interval spans midnight.
        // So we are open if now >= open OR now <= close
        return now >= openTime || now <= closeTime;
    } 
    // If current time is before close time (e.g. 1 AM), it's open. 
    // But since closeTime is set to "today", we need to be careful.
    // Let's rely on the previous logic: 
    // Interval [Open, Midnight] U [Midnight, Close]
    // So if close < open: Open if (now >= open) OR (now <= close)
    return now >= openTime || now <= closeTime;
  }

  // Standard case: Open 9 AM, Close 5 PM
  return now >= openTime && now <= closeTime;
};

const StoreInfo = () => {
  const { data, isLoading, fetchStoreData, selectedBranchId, setSelectedBranch } = useStoreInfo();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // تحميل بيانات المتجر مرة واحدة فقط عند التحميل الأول
  useEffect(() => {
    fetchStoreData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data?.workingHours) {
      const updateStatus = () => {
        const open = checkIsOpen(data.workingHours.open, data.workingHours.close, data.status);
        setIsOpen(open);
      };
      
      updateStatus();
      // Update every minute
      const interval = setInterval(updateStatus, 60000);
      return () => clearInterval(interval);
    }
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (isLoading || !data) {
    return (
      <S.InfoWrapper>
        <S.InfoContent>
          <S.StoreInfoHeader>
            <S.SkeletonStoreName />
            <S.SkeletonStatus />
          </S.StoreInfoHeader>
        </S.InfoContent>
        <S.StoreMeta>
          <S.StoreHours>
            <S.SkeletonHoursLabel />
            <S.SkeletonHoursValue />
            <S.SkeletonDeliveryNote />
          </S.StoreHours>
          <S.SkeletonBranchSelector />
        </S.StoreMeta>
      </S.InfoWrapper>
    );
  }

  const selectedBranch = data.branches.find(b => b.id === selectedBranchId) || data.branches[0];

  const handleBranchSelect = (id: number, comingSoon?: boolean) => {
    if (comingSoon) return; // منع الاختيار إذا كان قريباً
    setSelectedBranch(id);
    setIsDropdownOpen(false);
  };

  return (
    <S.InfoWrapper>
      <S.InfoContent>
        <S.StoreInfoHeader>
          <S.StoreName>{data.name}</S.StoreName>
          <S.StatusLabel $isOpen={isOpen}>
            {isOpen ? 'مفتوح' : 'مغلق'}
          </S.StatusLabel>
        </S.StoreInfoHeader>
      </S.InfoContent>
        
      <S.StoreMeta>
        <S.StoreHours>
          <S.HoursLabel>مواعيد الغلق والفتح</S.HoursLabel>
          <S.HoursValues>
            <span>مفتوح من: {formatWorkingHourDisplay(data.workingHours.open)}</span>
            <span className="separator">-</span>
            <span>يغلق عند: {formatWorkingHourDisplay(data.workingHours.close)}</span>
          </S.HoursValues>
          <S.DeliveryNote>
            اطلب في أي وقت، وسيتم التوصيل خلال أوقات العمل
          </S.DeliveryNote>
        </S.StoreHours>

        <S.BranchSelectorWrapper ref={dropdownRef}>
          <S.InputContainer onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            <S.BranchText>{selectedBranch?.name}</S.BranchText>
            <ChevronDown 
              size={14} 
              color="oklch(0.683 0 0)" 
              style={{ 
                transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }} 
            />
          </S.InputContainer>
          
          {isDropdownOpen && (
            <S.DropdownList>
              {data.branches.map((branch) => (
                <S.DropdownItem 
                  key={branch.id} 
                  $isSelected={branch.id === selectedBranchId}
                  $isDisabled={branch.comingSoon}
                  onClick={() => handleBranchSelect(branch.id, branch.comingSoon)}
                >
                  <span>{branch.name}</span>
                  {branch.comingSoon && <S.ComingSoonBadge>قريباً</S.ComingSoonBadge>}
                </S.DropdownItem>
              ))}
            </S.DropdownList>
          )}
        </S.BranchSelectorWrapper>
      </S.StoreMeta>
    </S.InfoWrapper>
  );
};

export default StoreInfo;
